import { useState } from "react";
import { UserCog, KeyRound, MonitorSmartphone, LogOut } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Section, Loadable, Empty, Info, ActionButton, RunButton, PageView, PageButtons, useLoad, fmt } from "@/components/control/kit";
import { PhotoAside } from "@/components/control/PublicShell";

const limitText = (s) => (s.state === "altbestand"
  ? `Altbestand ohne Ablaufregel · spätestens bis ${fmt(s.legacy_until)}`
  : `endet spätestens ${fmt(s.expires_at)} · nach 30 Minuten ohne Aktivität früher`);

function SessionChoice({ s, pick, setPick }) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#E6E1F0] bg-white p-4 has-[:checked]:border-brand-magenta" data-testid={`sessions-view-${s.id}`}>
      <input type="radio" name="session-pick" value={s.id} checked={pick === s.id} onChange={() => setPick(s.id)}
        className="mt-1 h-4 w-4 accent-[#D50B58]" data-testid={`sessions-view-pick-${s.id}`} />
      <span>
        <span className="block font-bold text-brand-purple">Weitere Sitzung</span>
        <span className="block text-sm text-zinc-600">{s.browser} · zuletzt aktiv {fmt(s.last_seen_at)} · Standort {s.location}</span>
        <span className="block text-xs text-zinc-600" data-testid={`sessions-view-limit-${s.id}`}>{limitText(s)}</span>
      </span>
    </label>
  );
}

const focusTitle = () => document.querySelector('[data-testid="sessions-view-page"] h1')?.focus();

// C98: select exactly one other own session, then confirm; the current session ends only via „Abmelden“.
function SessionsManage() {
  const st = useLoad("/account/sessions");
  const [pick, setPick] = useState("");
  return (
    <Loadable state={st}>
      {(d) => {
        const current = d.sessions.find((s) => s.current);
        const others = d.sessions.filter((s) => !s.current);
        const chosen = others.find((s) => s.id === pick);
        return (
          <>
            {d.legacy_session && <p className="rounded-xl bg-orange-50 p-3 text-sm text-orange-800" data-testid="sessions-view-legacy">Diese Sitzung stammt aus einer älteren Anmeldung und wird nicht einzeln geführt. Nach der nächsten Anmeldung erscheint sie in der Liste.</p>}
            {d.legacy_total > 0 && (
              <p className="rounded-xl border border-[#E3DDF0] bg-white p-3 text-sm text-zinc-700" data-testid="sessions-view-legacy-count">
                {d.legacy_total} Sitzung(en) stammen aus der Zeit vor der Ablaufregel (8 Stunden, 30 Minuten ohne Aktivität). Für sie gilt unverändert die bisherige Grenze von höchstens 7 Tagen ab Anmeldung. Eine Umstellung ist vorbereitet, aber noch nicht freigegeben.
              </p>
            )}
            {current && (
              <div className="rounded-2xl border border-[#E3DDF0] bg-[#EEEAF7] p-4" data-testid="sessions-view-current">
                <p className="font-bold text-brand-purple">Aktuelle Sitzung</p>
                <p className="text-sm text-zinc-600">Dieses Gerät · {current.browser} · angemeldet {fmt(current.created_at)}</p>
                <p className="text-xs text-zinc-600" data-testid="sessions-view-current-limit">{limitText(current)}</p>
                <p className="mt-1 text-xs text-zinc-600">Beenden nur über „Abmelden“.</p>
              </div>
            )}
            <fieldset className="space-y-3" data-testid="sessions-view-list">
              <legend className="sr-only">Weitere Sitzung auswählen</legend>
              {others.length === 0 ? <Empty text="Keine weiteren Sitzungen." testId="sessions-view-empty" />
                : others.map((s) => <SessionChoice key={s.id} s={s} pick={pick} setPick={setPick} />)}
            </fieldset>
            {d.total > d.sessions.length && (
              <p className="text-sm text-zinc-600" data-testid="sessions-view-more">{d.total - d.sessions.length} ältere Sitzung(en) werden nicht einzeln angezeigt. „Alle anderen Sitzungen beenden“ beendet auch diese.</p>
            )}
            <div className="flex flex-wrap gap-3 pt-1">
              <ActionButton label="Ausgewählte Sitzung beenden" icon={LogOut} variant="default" testId="sessions-view-revoke" disabled={!chosen}
                title="Sitzung beenden" submitLabel="Sitzung beenden" fields={[]}
                description={chosen ? `${chosen.browser} · zuletzt aktiv ${fmt(chosen.last_seen_at)} wird sofort abgemeldet.` : ""}
                onSubmit={async () => { await api.post(`/account/sessions/${chosen.id}/revoke`); return "Sitzung beendet."; }}
                onDone={() => { setPick(""); st.reload(); setTimeout(focusTitle, 150); }} />
              {others.length > 0 && <RunButton label="Alle anderen Sitzungen beenden" icon={LogOut} testId="sessions-view-revoke-others"
                success="Alle anderen Sitzungen wurden beendet." run={() => api.post("/account/sessions/revoke-others")} onDone={() => { setPick(""); st.reload(); }} />}
            </div>
          </>
        );
      }}
    </Loadable>
  );
}

function SessionsPage() {
  return (
    <PageView page="sitzungen" label="Sitzungen verwalten" icon={MonitorSmartphone} testId="sessions-view" title="Eigene Sitzungen verwalten"
      subtitle="Diese Liste betrifft ausschließlich deine eigenen Sitzungen.">
      {({ back }) => <><SessionsManage /><PageButtons back={back} testId="sessions-view" /></>}
    </PageView>
  );
}

function Sessions() {
  const st = useLoad("/account/sessions");
  return (
    <Section title={<span className="flex items-center gap-2"><MonitorSmartphone className="h-5 w-5 text-brand-magenta" /> Eigene Sitzungen</span>} testId="account-sessions"
      actions={<>
        <SessionsPage />
        {st.data && st.data.sessions.length > 1 && (
          <RunButton label="Alle anderen Sitzungen beenden" icon={LogOut} testId="revoke-other-sessions" success="Alle anderen Sitzungen wurden beendet."
            run={() => api.post("/account/sessions/revoke-others")} onDone={st.reload} />
        )}
      </>}>
      <Loadable state={st}>
        {(d) => (
          <>
            {d.legacy_session && <p className="mb-3 rounded-lg bg-orange-50 p-3 text-sm text-orange-800">Diese Sitzung stammt aus einer älteren Anmeldung und wird hier nicht einzeln geführt. Nach der nächsten Anmeldung erscheint sie in der Liste.</p>}
            {d.sessions.length === 0 ? <Empty text="Keine aktiven Sitzungen erfasst." /> : (
              <ul className="divide-y divide-border">
                {d.sessions.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-3" data-testid={`session-${s.id}`}>
                    <div>
                      <p className="font-medium text-brand-purple">{s.browser}{s.current && <span className="ml-2 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Diese Sitzung</span>}</p>
                      <p className="text-xs text-muted-foreground">Angemeldet {fmt(s.created_at)} · zuletzt aktiv {fmt(s.last_seen_at)} · Standort {s.location}</p>
                    </div>
                    {!s.current && <RunButton label="Beenden" icon={LogOut} testId={`revoke-session-${s.id}`} success="Sitzung beendet."
                      run={() => api.post(`/account/sessions/${s.id}/revoke`)} onDone={st.reload} />}
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </Loadable>
    </Section>
  );
}

export default function Account() {
  const { user } = useAuth();
  return (
    <div>
      <PageHeader title="Mein Konto" subtitle="Kontosicherheit, Sitzungen und Passwort" icon={UserCog} actions={
        <ActionButton label="Passwort ändern" icon={KeyRound} testId="change-password" submitLabel="Passwortänderung bestätigen"
          page="passwort" title="Eigenes Passwort ändern" pageSubtitle="Ändere nur dein eigenes Passwort mit erneuter Identitätsprüfung." pageAside={<PhotoAside />}
          description="Mindestens 10 Zeichen. Wir empfehlen, andere Sitzungen zu beenden."
          fields={[
            { name: "current_password", label: "Bisheriges Passwort", type: "password", required: true, autoComplete: "current-password" },
            { name: "new_password", label: "Neues Passwort", type: "password", required: true, autoComplete: "new-password", strength: true },
            { name: "repeat", label: "Neues Passwort wiederholen", type: "password", required: true, autoComplete: "new-password" },
            { name: "logout_others", label: "Alle anderen Sitzungen beenden", type: "checkbox", default: true },
          ]}
          onSubmit={async (v) => {
            if (v.new_password !== v.repeat) throw Object.assign(new Error(), { response: { status: 400, data: { detail: "Die beiden neuen Passwörter stimmen nicht überein." } } });
            await api.post("/account/password", { current_password: v.current_password, new_password: v.new_password, logout_others: v.logout_others });
            return "Passwort geändert.";
          }} />
      } />
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title="Konto" testId="account-info">
          <Info items={[["Name", user?.name], ["E-Mail", user?.email],
            ["Rolle (vom Server bestätigt)", <span key="r" data-testid="account-server-role">{user?.role_label}</span>],
            ["Kontozuordnung", <span key="i" data-testid="account-config-identity">{user?.config_identity?.label || "keine Zuordnung über Konfigurationsschlüssel"}</span>],
            ["Zweiter Faktor", "Authenticator-App (aktiv)"],
            ["Wiederherstellung", "Einmal-Wiederherstellungscodes – jeder Code ist nur einmal gültig."]]} />
        </Section>
        <Sessions />
      </div>
    </div>
  );
}
